import { interpolate } from "../engine/interpolate";
import { getOrCreateVisitorId } from "../../visitorId";
import { CampaignTracking } from "../../CampaignTracking";
import { ContactFormSubmitPayload } from "../../../components/ContactFormModal/ContactFormModal.types";

export interface WhatsappTrackingPayload extends Partial<
  Omit<ContactFormSubmitPayload, "campaign">
> {
  af_visitor_id?: string;
  page_url: string;
  unit_name?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

export interface WhatsappContextResult {
  interpolatedMessage: string;
  trackingPayload: WhatsappTrackingPayload;
}

export interface ResolveWhatsappContextArgs {
  rawMessage: string;
  formPayload?: ContactFormSubmitPayload;
  units?: { id: string; name: string }[];
}

/**
 * Especialista em contexto de WhatsApp.
 * Monta os dicionários de variáveis do Tracking, resolve a mensagem e devolve os dados prontos para uso no componente React.
 */
export const resolveWhatsappContext = ({
  rawMessage,
  formPayload,
  units,
}: ResolveWhatsappContextArgs): WhatsappContextResult => {
  const { campaign, ...formData } = formPayload || {};
  const visitorId = getOrCreateVisitorId();
  const trackingData = CampaignTracking.getTrackingData() || {};
  const pageUrl = window.location.href;

  // Pegar unidade selecionada se houver (ID)
  const selectedUnitStr = localStorage.getItem("selectedUnit");
  let unitName = "";

  if (selectedUnitStr && units) {
    const unit = units.find((u) => u.id === selectedUnitStr);
    if (unit) {
      unitName = unit.name;
    }
  }

  // Montar payload de tracking base
  const trackingPayload: WhatsappTrackingPayload = {
    ...(visitorId ? { af_visitor_id: visitorId } : {}),
    page_url: pageUrl,
    unit_name: unitName,
    ...trackingData, // utm_source, utm_medium, utm_campaign, etc.
    ...(formData || {}), // mesclar dados do form, se existirem
  };

  // O dicionário de contexto é exatamente o tracking payload (chaves iguais)
  // Mas garantindo que tudo seja string para a interpolação.
  const stringContext: Record<string, string> = {};
  (
    Object.keys(trackingPayload) as Array<keyof WhatsappTrackingPayload>
  ).forEach((key) => {
    const val = trackingPayload[key];
    if (val !== undefined && val !== null) {
      stringContext[key] = String(val);
    }
  });

  const interpolatedMessage = interpolate({
    rawString: rawMessage,
    context: stringContext,
  });

  return {
    interpolatedMessage,
    trackingPayload,
  };
};
